import React, { useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import InteractiveChart from './InteractiveChart';
import { ChartConfiguration } from 'chart.js';

// Error boundary for chart rendering
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; chartIndex: number },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; chartIndex: number }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ChartErrorBoundary] Chart rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--danger-soft)',
          borderRadius: '8px',
          border: '1px solid var(--cg-terra-200)',
          marginTop: '1rem',
          marginBottom: '1rem'
        }}>
          <strong>⚠️ Chart Rendering Error</strong>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--fg-3)' }}>
            Unable to display chart. {this.state.error?.message || 'Unknown error'}
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ChartConfig {
  type: 'chart_config';
  chartType: 'line' | 'bar' | 'pie' | 'donut' | 'area' | 'scatter' | 'radar' | 'bubble';
  config: ChartConfiguration;
  title: string;
  width?: number;
  height?: number;
  horizontal?: boolean;
  stacked?: boolean;
}

interface MarkdownWithChartsProps {
  content: string;
  onChartExport?: (chartTitle: string, base64Image: string) => void;
}

/**
 * Checks if a line looks like a GFM table header row (has 2+ pipes).
 */
const isTableHeaderRow = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return false;
  const pipeCount = (trimmed.match(/\|/g) || []).length;
  return pipeCount >= 2 && (trimmed.startsWith('|') || trimmed.endsWith('|'));
};

/**
 * Checks if a line is a GFM table delimiter row (e.g. |---|---|).
 */
const isTableDelimiterRow = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed.includes('|') || !trimmed.includes('-')) return false;
  // Strip outer pipes and split into cells
  const inner = trimmed.replace(/^\||\|$/g, '');
  const cells = inner.split('|');
  // Every cell must be a valid delimiter: optional colons around dashes
  return cells.length >= 1 && cells.every(cell => /^\s*:?-+:?\s*$/.test(cell));
};

/**
 * Ensures blank lines before GFM tables so remark-gfm parses them correctly.
 * Without a blank line before a table, remark-gfm treats pipes as inline text.
 */
const preprocessMarkdownTables = (content: string): string => {
  // Fast path: no pipes means no tables
  if (!content.includes('|')) return content;

  const lines = content.split('\n');
  const result: string[] = [];
  let inCodeBlock = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Track fenced code blocks to avoid modifying tables inside them
    if (/^```|^~~~/.test(line.trim())) {
      inCodeBlock = !inCodeBlock;
    }

    if (!inCodeBlock && i + 1 < lines.length) {
      // Check if current line is a table header and next line is a delimiter
      if (isTableHeaderRow(line) && isTableDelimiterRow(lines[i + 1])) {
        // Insert blank line before table if previous line is non-empty
        if (result.length > 0 && result[result.length - 1].trim() !== '') {
          result.push('');
        }
      }
    }

    result.push(line);
  }

  return result.join('\n');
};

/**
 * Parses markdown content and extracts chart configurations
 * from ```chart code blocks
 */
const parseContentWithCharts = (content: string): Array<{ type: 'text' | 'chart'; data: string | ChartConfig }> => {
  const parts: Array<{ type: 'text' | 'chart'; data: string | ChartConfig }> = [];

  // Regex to match ```chart ... ``` blocks
  const chartBlockRegex = /```chart\n([\s\S]*?)\n```/g;

  let lastIndex = 0;
  let match;

  while ((match = chartBlockRegex.exec(content)) !== null) {
    // Add text before this chart
    if (match.index > lastIndex) {
      const textBefore = content.substring(lastIndex, match.index);
      if (textBefore.trim()) {
        parts.push({ type: 'text', data: textBefore });
      }
    }

    // Parse the chart JSON
    try {
      const chartJson = match[1];
      const chartConfig: ChartConfig = JSON.parse(chartJson);

      // Validate it's actually a chart config
      if (chartConfig.type === 'chart_config' && chartConfig.config && chartConfig.chartType) {
        parts.push({ type: 'chart', data: chartConfig });
      } else {
        // If it's not a valid chart, treat as regular code block
        parts.push({ type: 'text', data: match[0] });
      }
    } catch (error) {
      console.error('[MarkdownWithCharts] Failed to parse chart JSON:', error);
      // If parsing fails, treat as regular code block
      parts.push({ type: 'text', data: match[0] });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last chart
  if (lastIndex < content.length) {
    const textAfter = content.substring(lastIndex);
    if (textAfter.trim()) {
      parts.push({ type: 'text', data: textAfter });
    }
  }

  // If no charts found, return the whole content as text
  if (parts.length === 0) {
    parts.push({ type: 'text', data: content });
  }

  return parts;
};

/**
 * Converts a <table> DOM element to CSV text.
 */
const tableToCsv = (table: HTMLTableElement): string => {
  const rows: string[][] = [];
  const tableRows = table.querySelectorAll('tr');
  tableRows.forEach((row) => {
    const cells: string[] = [];
    row.querySelectorAll('th, td').forEach((cell) => {
      let value = (cell as HTMLElement).textContent || '';
      // Escape values containing commas, quotes, or newlines
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        value = '"' + value.replace(/"/g, '""') + '"';
      }
      cells.push(value);
    });
    rows.push(cells);
  });
  return rows.map((r) => r.join(',')).join('\n');
};

/**
 * Wraps a table with a copy-to-CSV button.
 */
const CopyableTable: React.FC<{ children: React.ReactNode; tableProps: Record<string, any> }> = ({ children, tableProps }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    if (!tableRef.current) return;
    const csv = tableToCsv(tableRef.current);
    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[CopyableTable] Failed to copy CSV:', err);
    }
  }, []);

  return (
    <div className="table-wrapper">
      <button
        className={`copy-csv-button${copied ? ' copied' : ''}`}
        onClick={handleCopy}
        title="Copy table as CSV"
      >
        {copied ? 'Copied!' : 'Copy CSV'}
      </button>
      <table className="markdown-table" ref={tableRef} {...tableProps}>{children}</table>
    </div>
  );
};

const MarkdownWithCharts: React.FC<MarkdownWithChartsProps> = ({ content, onChartExport }) => {
  // Safety check for content
  if (!content || typeof content !== 'string') {
    console.error('[MarkdownWithCharts] Invalid content received:', content);
    return <div>Error: Invalid content</div>;
  }

  // Preprocess to ensure blank lines before GFM tables
  const processedContent = preprocessMarkdownTables(content);

  let parts: Array<{ type: 'text' | 'chart'; data: string | ChartConfig }> = [];

  try {
    parts = parseContentWithCharts(processedContent);
  } catch (error) {
    console.error('[MarkdownWithCharts] Error parsing content:', error);
    // Fallback to rendering as plain markdown
    return (
      <div className="markdown-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
            img: ({ node, ...props }) => (
              <img {...props} alt={props.alt || 'Image'} loading="lazy" />
            ),
            table: ({ node, children, ...props }) => (
              <CopyableTable tableProps={props}>{children}</CopyableTable>
            ),
            thead: ({ node, children, ...props }) => (
              <thead className="markdown-thead" {...props}>{children}</thead>
            ),
            tbody: ({ node, children, ...props }) => (
              <tbody className="markdown-tbody" {...props}>{children}</tbody>
            ),
            tr: ({ node, children, ...props }) => (
              <tr className="markdown-tr" {...props}>{children}</tr>
            ),
            th: ({ node, children, ...props }) => (
              <th className="markdown-th" {...props}>{children}</th>
            ),
            td: ({ node, children, ...props }) => (
              <td className="markdown-td" {...props}>{children}</td>
            )
          }}
        >
          {processedContent}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div>
      {parts.map((part, index) => {
        try {
          if (part.type === 'chart') {
            const chartConfig = part.data as ChartConfig;

            // Validate chart config
            if (!chartConfig || !chartConfig.config || !chartConfig.chartType || !chartConfig.title) {
              console.error('[MarkdownWithCharts] Invalid chart config at index', index, chartConfig);
              return (
                <div key={`chart-error-${index}`} style={{
                  padding: '1rem',
                  backgroundColor: 'var(--danger-soft)',
                  borderRadius: '8px',
                  border: '1px solid var(--cg-terra-200)',
                  marginBottom: '1rem'
                }}>
                  ⚠️ Chart rendering error: Invalid configuration
                </div>
              );
            }

            return (
              <ChartErrorBoundary key={`chart-boundary-${index}`} chartIndex={index}>
                <InteractiveChart
                  config={chartConfig.config}
                  title={chartConfig.title}
                  chartType={chartConfig.chartType}
                  width={chartConfig.width}
                  height={chartConfig.height}
                  onExport={(base64Image) => {
                    if (onChartExport) {
                      onChartExport(chartConfig.title, base64Image);
                    }
                  }}
                />
              </ChartErrorBoundary>
            );
          } else {
            return (
              <div key={`text-${index}`} className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ node, children, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                    img: ({ node, ...props }) => (
                      <img {...props} alt={props.alt || 'Image'} loading="lazy" />
                    ),
                    table: ({ node, children, ...props }) => (
                      <CopyableTable tableProps={props}>{children}</CopyableTable>
                    ),
                    thead: ({ node, children, ...props }) => (
                      <thead className="markdown-thead" {...props}>{children}</thead>
                    ),
                    tbody: ({ node, children, ...props }) => (
                      <tbody className="markdown-tbody" {...props}>{children}</tbody>
                    ),
                    tr: ({ node, children, ...props }) => (
                      <tr className="markdown-tr" {...props}>{children}</tr>
                    ),
                    th: ({ node, children, ...props }) => (
                      <th className="markdown-th" {...props}>{children}</th>
                    ),
                    td: ({ node, children, ...props }) => (
                      <td className="markdown-td" {...props}>{children}</td>
                    )
                  }}
                >
                  {part.data as string}
                </ReactMarkdown>
              </div>
            );
          }
        } catch (error) {
          console.error('[MarkdownWithCharts] Error rendering part at index', index, error);
          return (
            <div key={`error-${index}`} style={{
              padding: '1rem',
              backgroundColor: 'var(--danger-soft)',
              borderRadius: '8px',
              border: '1px solid var(--cg-terra-200)',
              marginBottom: '1rem'
            }}>
              ⚠️ Rendering error at position {index}
            </div>
          );
        }
      })}
    </div>
  );
};

export default MarkdownWithCharts;
